import { isNonEmpty } from '@sgme/fp';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { Action } from 'state/actions';
import { addKey, addKeys, patchKey, patchKeys, removeKeys, renameKey } from 'state/utils/collection';
import { isExternalQuoteId } from 'utils/string';

import type { BatchFormsCloneRequested } from '../batchForm.action';
import * as actionTypes from '../batchForm.actionTypes';
import { hasErrorSeverity } from './infos.helpers';
import type { BatchFormInfos, BatchFormInfosStateMap } from './infos.model';

export function infosReducer(state: BatchFormInfosStateMap = {}, action: Action): BatchFormInfosStateMap {
  switch (action.type) {
    case actionTypes.DEFAULT_BATCH_FORM_REQUESTED:
    case actionTypes.BATCH_IMPORT_FORM_REQUESTED: {
      const { temporaryFormId, productType, productSubType, wrapper } = action;
      return addKey(state, temporaryFormId, {
        ...createNewForm(temporaryFormId, productType, productSubType, wrapper),
        status: 'INITIALIZING',
      });
    }

    case actionTypes.BATCH_FORM_RELOAD_REQUESTED: {
      const { temporaryFormId, productType, productSubType, wrapper, identifier, idType } = action;
      const externalQuoteId = getExternalQuoteIdFromReloadRequest(identifier, idType);
      return addKey(state, temporaryFormId, {
        ...createNewForm(temporaryFormId, productType, productSubType, wrapper),
        status: 'REPRICING',
        ...(externalQuoteId && { quoteId: externalQuoteId }),
      });
    }

    case actionTypes.BATCH_FORMS_RESTORE_REQUESTED:
      return addKeys(
        state,
        action.forms.map(form => form.negotiationId),
        action.forms.map(
          ({ negotiationId, quoteId }) =>
            <BatchFormInfos>{
              actions: [],
              isSelected: false,
              negotiationId,
              quoteId,
              productView: 'Batch',
              quoteQuery: {},
              status: 'RELOADING',
              productType: action.productType,
              productSubType: action.productSubType,
              wrapper: action.wrapper,
              version: 0,
            },
        ),
      );

    case actionTypes.DEFAULT_BATCH_FORM_RECEIVED: {
      const {
        actions,
        negotiationId,
        productView,
        quoteQuery,
        temporaryFormId,
        validationErrors,
        version,
        values,
        mustForceAdvancedMode,
      } = action;
      const patchedStateForInitialization = patchKey(state, temporaryFormId, () => ({
        actions,
        negotiationId,
        productView,
        quoteQuery,
        validationErrors,
        version,
        productSubType: values.ProductSubtype as string,
        productType: values.ProductType as string,
        status: hasErrorSeverity(validationErrors) ? ('NOT_VALID' as const) : ('READY' as const),
        mustForceAdvancedMode,
      }));

      if (temporaryFormId === negotiationId) {
        return patchedStateForInitialization;
      }
      return renameKey(patchedStateForInitialization, temporaryFormId, negotiationId);
    }

    case actionTypes.DEFAULT_BATCH_FORM_FAILED: {
      const { temporaryFormId } = action;
      return patchKey(state, temporaryFormId, info => ({
        ...info,
        status: 'INITIALIZATION_ERROR' as const,
      }));
    }

    case actionTypes.BATCH_FORM_RELOAD_FAILED: {
      const { temporaryFormId } = action;
      return patchKey(state, temporaryFormId, info => ({
        ...info,
        status: 'REPRICE_ERROR' as const,
      }));
    }

    case actionTypes.BATCH_FORM_SELECT_TOGGLED:
      return patchKey(state, action.formId, previousState => ({
        isSelected: !previousState.isSelected,
      }));

    case actionTypes.BATCH_FORM_SELECT_ALL_TOGGLED:
      return patchKeys(state, action.formIds, previousState =>
        previousState.status !== 'QUOTING' && previousState.status !== 'DUPLICATING'
          ? {
              isSelected: action.value,
            }
          : {},
      );

    case 'FORM_ENRICHMENT_REQUESTED': {
      if (action.workspace !== 'newBatch') {
        return state;
      }
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'PATCHING' as const,
      }));
    }

    case actionTypes.BATCH_FORM_ROLLBACK_REQUESTED: {
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'RELOADING' as const,
      }));
    }

    case actionTypes.BATCH_FORM_ENRICHMENT_RECEIVED: {
      const { formId, version, actions, quoteQuery, validationErrors, productSubtype, mustForceAdvancedMode } = action;
      return patchKey(state, formId, info => ({
        ...info,
        productSubType: productSubtype,
        actions,
        quoteQuery,
        validationErrors,
        version,
        status: hasErrorSeverity(validationErrors) ? ('NOT_VALID' as const) : ('READY' as const),
        mustForceAdvancedMode: mustForceAdvancedMode,
      }));
    }

    case actionTypes.BATCH_FORM_ENRICHMENT_FAILED: {
      const { formId } = action;
      return patchKey(state, formId, info => ({
        ...info,
        status: 'PATCH_ERROR' as const,
      }));
    }

    case actionTypes.BATCH_FORMS_DELETED:
      return removeKeys(state, action.formIds);

    case actionTypes.BATCH_QUOTE_ADD_TO_QUEUE:
      return patchKeys(state, action.formIds, () => ({
        status: 'QUOTING' as const,
        isSelected: false,
      }));

    case actionTypes.BATCH_QUOTE_RECEIVED:
      return patchKey(state, action.formId, () => ({
        status: isNonEmpty(action.quote.rejections) ? ('REJECTED' as const) : ('QUOTED' as const),
      }));

    case actionTypes.BATCH_QUOTE_EXPIRED:
      return patchKey(state, action.formId, () => ({
        status: 'QUOTED_EXPIRED' as const,
      }));

    case actionTypes.BATCH_PREVIOUS_QUOTE_LOADED:
      return patchKey(state, action.formId, () => ({
        quoteId: action.quoteId,
        status: isNonEmpty(action.quote.rejections) ? ('REJECTED' as const) : ('QUOTED_EXPIRED' as const),
      }));

    case actionTypes.BATCH_QUOTE_ACKED:
      return action.forms.reduce((result: BatchFormInfosStateMap, form: QuoteAck) => {
        return patchKey(result, form.negotiationId, () => ({
          version: form.newVersion,
          quoteId: form.quoteId,
        }));
      }, state);

    case actionTypes.BATCH_QUOTE_REQUEST_FAILED:
      return action.forms.reduce(
        (result: BatchFormInfosStateMap, form: { formId: string; newVersion?: number }) =>
          patchKey(result, form.formId, previousState => ({
            status: 'READY' as const,
            version: form.newVersion ?? previousState.version,
          })),
        state,
      );

    case actionTypes.BATCH_QUOTE_RESULT_FAILED:
      return patchKey(state, action.formId, () => ({
        status: 'READY' as const,
        quoteId: undefined,
      }));

    // TODO: Unit test
    case actionTypes.BATCH_FORMS_CLONE_REQUESTED:
      return onFormCloneRequested(state, action);
    // TODO: Unit test
    case actionTypes.BATCH_FORM_QUOTE_RELOAD_REQUESTED:
      return addKey(state, action.temporaryFormId, <BatchFormInfos>{
        actions: [],
        isSelected: false,
        negotiationId: action.temporaryFormId,
        productView: 'Batch',
        quoteQuery: {},
        status: 'REPRICING',
        productType: action.productType,
        productSubType: action.productSubType,
        wrapper: action.wrapper,
        quoteId: action.quoteId,
        version: 0,
      });

    case actionTypes.BATCH_QUOTE_REMOVED:
      return patchKeys(state, action.formIds, () => ({
        quoteId: undefined,
        quoteType: undefined,
      }));

    case actionTypes.BATCH_QUOTE_ABORTED:
      return patchKey(state, action.formId, () => ({
        quoteId: undefined,
        quoteType: undefined,
        status: 'READY' as const,
      }));

    default:
      return state;
  }
}

function createNewForm(
  negotiationId: string,
  productType: string,
  productSubType: string,
  wrapper: string,
): Omit<BatchFormInfos, 'status'> {
  return {
    actions: [],
    isSelected: false,
    negotiationId,
    productView: 'Batch',
    quoteQuery: {},
    productType: productType,
    productSubType: productSubType,
    wrapper: wrapper,
    version: 0,
    mustForceAdvancedMode: false,
  };
}

function onFormCloneRequested(
  state: BatchFormInfosStateMap,
  { temporaryFormIds, productSubType, wrapper, mustForceAdvancedMode }: BatchFormsCloneRequested,
): BatchFormInfosStateMap {
  const values = temporaryFormIds.map(
    id =>
      <BatchFormInfos>{
        productType: '',
        productSubType: productSubType,
        wrapper: wrapper,
        actions: [],
        isSelected: false,
        negotiationId: id,
        productView: 'Batch',
        quoteQuery: {},
        status: 'DUPLICATING',
        version: 0,
        mustForceAdvancedMode: mustForceAdvancedMode,
      },
  );
  return addKeys(state, temporaryFormIds, values);
}

function getExternalQuoteIdFromReloadRequest(identifier: string, idType: IdType): string | undefined {
  return idType === IdType.ExternalQuoteId || isExternalQuoteId(identifier) ? identifier : undefined;
}
